import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@common/checkbox";
import {
  useExportData,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectElementDetailsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveWbsConfiguration,
} from "@planzone/view-models";
import {
  ExportsDataTypes,
  ExportsFormats,
  Sort as S,
  wbsFilter,
  project,
} from "@planzone/dto";

import { Props, ReportType } from "./types";
import { Button } from "@common/button";
import { DateTimeInput } from "@common/dateTimeInput";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { ViewSelect } from "./viewSelect";
import { ToggleArrow } from "./viewSelect/toggleArrow";
import { Filters } from "./filters";
import { Sort } from "./sort";
import styles from "./styles.scss";
import viewSelectStyles from "./viewSelect/styles.scss";
import { useRoles } from "@scenes/safeguard";

function TimeSheetByTaskReport(props: Props) {
  const [selectedReportType, setSelectedReportType] = useState<ReportType[]>([
    "monthly",
  ]);
  const [sort, setSort] = React.useState<S | null>(null);
  const [filter, setFilter] = React.useState(wbsFilter);
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [filterByResourceIntegrationDate, setFilterByResourceIntegrationDate] =
    useState(false);
  const [filterByProjectIntegrationDate, setFilterByProjectIntegrationDate] =
    useState(false);
  const { isRequestPending, exportData } = useExportData();
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { configuration } = useRetrieveWbsConfiguration(planzone);
  const roles = useRoles({});

  return (
    <div
      className={classNames(styles.wbsReport, {
        [styles.checked]: isChecked,
      })}
    >
      <div className={styles.row}>
        <div
          className={styles.checkbox}
          onClick={() => setIsChecked(!isChecked)}
        >
          <Checkbox isChecked={isChecked} />
        </div>
        <div className={styles.col}>
          <h2 className={styles.name}>
            {t("exportReportsPage.timeSheetByTaskReport.title")}
          </h2>
          <p className={styles.description}>
            {t("exportReportsPage.timeSheetByTaskReport.subTitle")}
          </p>
        </div>
        {isChecked && (
          <Button
            className={styles.button}
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={!startDate || !endDate}
            press={() => {
              exportData({
                fileName: props.fileName,
                author: props.author,
                data: {
                  type: ExportsDataTypes.TASK_TIMESHEET_REPORT,
                  data: {
                    planzone: props.planzone,
                    projects: [],
                    startDate: startDate,
                    endDate: endDate,
                    reportType: selectedReportType,
                    sort: sort ? [sort] : undefined,
                    filter,
                    template,
                    configuration,
                    roles,
                    filterByResourceIntegrationDate,
                    filterByProjectIntegrationDate,
                  },
                },
                format: ExportsFormats.EXCEL,
              });
            }}
          >
            {t("exportReportsPage.download")}
          </Button>
        )}
      </div>
      {isChecked && (
        <div className={styles.filters}>
          <ViewSelect
            selectedReportType={selectedReportType}
            selectDataType={(type) => {
              setSelectedReportType([...selectedReportType, type]);
            }}
            unSelectDataType={(type: ReportType) => {
              setSelectedReportType(
                selectedReportType.filter((t) => t !== type),
              );
            }}
          />
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.timeSheetByTaskReport.startDate")}
            </h3>
            <DateTimeInput
              dateTime={startDate}
              placeholder={t(
                "exportReportsPage.timeSheetByTaskReport.startDate",
              )}
              input={setStartDate}
            />
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.timeSheetByTaskReport.endDate")}
            </h3>
            <DateTimeInput
              dateTime={endDate}
              placeholder={t(
                "exportReportsPage.timeSheetByTaskReport.startDate",
              )}
              input={setEndDate}
            />
          </div>
        </div>
      )}
      {isChecked && (
        <div className={styles.filters}>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.timeSheetByTaskReport.exportLabel")}
            </h3>
            <VisibilityWatcher>
              {(isVisible, show, hide) => (
                <div
                  className={classNames(viewSelectStyles.dataTypeFilter, {
                    [viewSelectStyles.opened]: isVisible,
                  })}
                >
                  <div
                    className={classNames(
                      viewSelectStyles.input,
                      styles.integrationDateFilterInput,
                    )}
                    onClick={isVisible ? hide : show}
                  >
                    <p className={viewSelectStyles.selectedDataTypes}>
                      {filterByResourceIntegrationDate
                        ? t(
                            "exportReportsPage.timeSheetByTaskReport.filterByResourceIntegrationDate",
                          )
                        : filterByProjectIntegrationDate
                          ? t(
                              "exportReportsPage.timeSheetByTaskReport.filterByProjectIntegrationDate",
                            )
                          : t(
                              "exportReportsPage.timeSheetByTaskReport.integrationDateFilterAll",
                            )}
                    </p>
                    <ToggleArrow className={viewSelectStyles.toggleArrow} />
                  </div>
                  {isVisible && (
                    <div className={viewSelectStyles.list}>
                      <div
                        className={viewSelectStyles.item}
                        onClick={() => {
                          setFilterByResourceIntegrationDate(false);
                          setFilterByProjectIntegrationDate(false);
                          hide();
                        }}
                      >
                        <Checkbox
                          isChecked={
                            !filterByResourceIntegrationDate &&
                            !filterByProjectIntegrationDate
                          }
                        />
                        <p
                          className={classNames(
                            viewSelectStyles.typeName,
                            styles.integrationDateFilterTypeName,
                          )}
                        >
                          {t(
                            "exportReportsPage.timeSheetByTaskReport.integrationDateFilterAll",
                          )}
                        </p>
                      </div>
                      <div
                        className={viewSelectStyles.item}
                        onClick={() => {
                          setFilterByResourceIntegrationDate(true);
                          setFilterByProjectIntegrationDate(false);
                          hide();
                        }}
                      >
                        <Checkbox isChecked={filterByResourceIntegrationDate} />
                        <p
                          className={classNames(
                            viewSelectStyles.typeName,
                            styles.integrationDateFilterTypeName,
                          )}
                        >
                          {t(
                            "exportReportsPage.timeSheetByTaskReport.filterByResourceIntegrationDate",
                          )}
                        </p>
                      </div>
                      <div
                        className={viewSelectStyles.item}
                        onClick={() => {
                          setFilterByProjectIntegrationDate(true);
                          setFilterByResourceIntegrationDate(false);
                          hide();
                        }}
                      >
                        <Checkbox isChecked={filterByProjectIntegrationDate} />
                        <p
                          className={classNames(
                            viewSelectStyles.typeName,
                            styles.integrationDateFilterTypeName,
                          )}
                        >
                          {t(
                            "exportReportsPage.timeSheetByTaskReport.filterByProjectIntegrationDate",
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </VisibilityWatcher>
          </div>
          <Sort sort={sort} select={setSort} />
          <Filters project={project} filter={filter} setFilter={setFilter} />
        </div>
      )}
    </div>
  );
}

export { TimeSheetByTaskReport };
